import { isMobile } from "react-device-detect";
import { initializeApp } from "firebase/app";
import {
  fetchAndActivate,
  getRemoteConfig,
  getString
} from "firebase/remote-config";
import {
  FETCH_TIMEOUT_MILLIS,
  MINIMUM_FETCH_INTERVAL,
  SB_ENV
} from "@config/common";
import {
  DeviceType,
  FirebaseGenderType,
  FirebaseWishType,
  GenderObjType,
  Media,
  WishObjType
} from "@type/index";
import store from "../../redux/store";
import { filterWhitespaceAndNBSP } from "../../util/helpers";

export enum FeatureFlagKeys {
  STYLINGBOOK_SB_TAB_GENDER = "stylingbook_sb_tab_gender",
  STYLINGBOOK_SH_TAB_GENDER = "stylingbook_sh_tab_gender",
  STYLINGBOOK_SB_WISH = "stylingbook_sb_wish",
  STYLINGBOOK_SH_WISH = "stylingbook_sh_wish",
  PRICE_CHANGE_HISTORY = "nav_tpch"
}

export type FeatureFlags = {
  [key in FeatureFlagKeys]: FeatureFlagValue;
};

export type FeatureFlagValue = string | number | boolean;

// Firebase project configuration
const firebaseConfig = (brand: string, country: string) => ({
  apiKey: SB_ENV(brand, country).firebaseApiKey,
  projectId: SB_ENV(brand, country).firebaseProjectId,
  appId: SB_ENV(brand, country).firebaseAppId,
  measurementId: SB_ENV(brand, country).firebaseMeasurementId
});

// One Firebase app instance per brand-region. A default (unnamed) app cannot be
// reused across brand/region because its options differ (initializeApp would throw).
const appMap = new Map<string, ReturnType<typeof initializeApp>>();

const getOrCreateApp = (brand: string, country: string) => {
  const appName = `${brand}-${country}`;
  let app = appMap.get(appName);
  if (!app) {
    app = initializeApp(firebaseConfig(brand, country), appName);
    appMap.set(appName, app);
  }
  return app;
};

// Firebase get remote config timeout
function waitWithTimeout(promise: any, timeoutMessage = "timeout") {
  let timer: NodeJS.Timeout;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(timeoutMessage), FETCH_TIMEOUT_MILLIS);
  });
  return Promise.race([timeoutPromise, promise]).finally(() =>
    clearTimeout(timer)
  );
}

export const getFirebaseConfig = async () => {
  const { region, brand_name } = store.getState().config;
  // Initialize Firebase app for the current brand-region
  const firebaseApp = getOrCreateApp(brand_name, region);
  try {
    // Initialize Remote Config and get a reference to the service
    const remoteConfig = getRemoteConfig(firebaseApp);
    remoteConfig.settings.fetchTimeoutMillis = FETCH_TIMEOUT_MILLIS; // TODO not work
    remoteConfig.settings.minimumFetchIntervalMillis = MINIMUM_FETCH_INTERVAL;
    try {
      await waitWithTimeout(fetchAndActivate(remoteConfig));
    } catch (e) {
      // fetchAndActivate failed, getString will fallback to local cache
    }
    const sb_tab_gender: string = getString(
      remoteConfig,
      FeatureFlagKeys.STYLINGBOOK_SB_TAB_GENDER
    );
    const sh_tab_gender: string = getString(
      remoteConfig,
      FeatureFlagKeys.STYLINGBOOK_SH_TAB_GENDER
    );
    const sb_wish: string = getString(
      remoteConfig,
      FeatureFlagKeys.STYLINGBOOK_SB_WISH
    );
    const sh_wish: string = getString(
      remoteConfig,
      FeatureFlagKeys.STYLINGBOOK_SH_WISH
    );
    const price_change_history: string = getString(
      remoteConfig,
      FeatureFlagKeys.PRICE_CHANGE_HISTORY
    );
    // Get gender tab info
    let genderByBrand: FirebaseGenderType | undefined;
    try {
      const _sb: GenderObjType = JSON.parse(
        filterWhitespaceAndNBSP(sb_tab_gender)
      );
      const _sh: GenderObjType = JSON.parse(
        filterWhitespaceAndNBSP(sh_tab_gender)
      );
      genderByBrand = {
        sb: _sb,
        sh: _sh
      };
    } catch (e) {
      genderByBrand = undefined;
    }
    // Get wish icon and wish count info
    let wishByBrand: FirebaseWishType | undefined;
    try {
      const _sb: WishObjType = JSON.parse(sb_wish);
      const _sh: WishObjType = JSON.parse(sh_wish);
      wishByBrand = {
        sb: _sb,
        sh: _sh
      };
    } catch (e) {
      wishByBrand = undefined;
    }
    return {
      web_tab_gender: genderByBrand,
      web_wish: wishByBrand,
      price_change_history: price_change_history
    };
  } catch (e) {
    return null;
  }
};

// Analytics is not required to render the page, so load it after the initial work is complete.
export const warmFirebaseAnalytics = () => {
  const initializeAnalytics = async () => {
    const { language, isApp, brand_name, region } = store.getState().config;
    const { getAnalytics, setUserProperties } = await import(
      "firebase/analytics"
    );
    const analytics = getAnalytics(getOrCreateApp(brand_name, region));
    setUserProperties(analytics, {
      language,
      platform: isApp ? Media.APP : Media.WEB,
      device: isMobile ? DeviceType.SP : DeviceType.PC,
      environment: process.env.REACT_APP_ENV
    });
  };

  if (typeof window !== "undefined") {
    window.setTimeout(() => {
      initializeAnalytics().catch(() => undefined);
    }, 2000);
  }
};

let firebaseConfigCache: {
  key: string;
  promise: Promise<Awaited<ReturnType<typeof getFirebaseConfig>>>;
} | null = null;

export const warmFirebaseConfig = () => {
  const { region, brand_name } = store.getState().config;
  const key = `${brand_name}-${region}`;
  if (firebaseConfigCache && firebaseConfigCache.key === key) {
    return firebaseConfigCache.promise;
  }

  firebaseConfigCache = {
    key,
    promise: getFirebaseConfig()
  };

  return firebaseConfigCache.promise;
};
